import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';

import { Analytics } from '@vercel/analytics/react';
import { ximiOidcConfig } from './utilities/ximi/oidcConfig';
import { UserProvider } from './providers/UserProvider';
import { isPlatform } from './utilities/platform';
import * as Sentry from '@sentry/react';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';

/* Theme variables */
import './theme/variables.index.css';
import { Router } from './routes';
import { ThemeConfig } from './theme';
import { WindowProvider } from './providers/WindowProvider';
import { MenuProvider } from './providers/MenuProvider';
import { PRODUCTION } from './config';
import { useEffect } from 'react';
import { SafeAreaDebug } from './components/SafeAreaDebug';
import { enableImmersiveMode } from './utilities/systemBars';
import AppToaster from './components/AppToaster';
import { reportXimiStimulinUsageIfDue } from './stores/ximi/ximiStimulinUsage';

config.autoAddCss = false;

export default function App(): JSX.Element {
    useEffect(() => {
        // Les insets safe-area sont fournis par env() (iOS/web, cf. main.css) et par
        // l'injection native du core Capacitor sur Android — plus aucune gestion JS ici.
        // Politique unique : mode immersif total (cf. utilities/systemBars.ts),
        // ré-appliqué au retour au premier plan car Android peut ré-afficher les barres
        if (!isPlatform('hybrid')) {
            return undefined;
        }

        enableImmersiveMode();

        const reassertImmersiveMode = () => {
            if (!document.hidden) {
                enableImmersiveMode();
            }
        };
        document.addEventListener('visibilitychange', reassertImmersiveMode);

        return () => {
            document.removeEventListener('visibilitychange', reassertImmersiveMode);
        };
    }, []);

    // Back to foreground (native and web): declare today's Stimul'In usage to Ozmo if due.
    useEffect(() => {
        const onForeground = (): void => {
            if (!document.hidden) void reportXimiStimulinUsageIfDue();
        };
        document.addEventListener('visibilitychange', onForeground);
        return () => document.removeEventListener('visibilitychange', onForeground);
    }, []);

    return (
        <Sentry.ErrorBoundary fallback={({ error }) => <div>{error.toString()}</div>}>
            <div
                className={isPlatform('hybrid') ? 'safe-area-container' : ''}
                style={{
                    height: '100dvh',
                    width: '100%'
                }}>
                {/* Composant de debug - À RETIRER EN PRODUCTION */}
                {/* <SafeAreaDebug /> */}

                <ThemeConfig>
                    <BrowserRouter>
                        <AuthProvider {...ximiOidcConfig}>
                            <UserProvider>
                                <WindowProvider>
                                    <MenuProvider>
                                        <Router />
                                    </MenuProvider>
                                </WindowProvider>
                            </UserProvider>
                        </AuthProvider>
                    </BrowserRouter>
                </ThemeConfig>
                <AppToaster />
            </div>
            {PRODUCTION && <Analytics />}
        </Sentry.ErrorBoundary>
    );
}
